import { AnimatePresence, motion } from 'motion/react';
import { FilePen } from 'lucide-react';
import { staggerItem } from '@/lib/motion';
import { SidebarSection } from '@reflex/ui/components/sidebar/SidebarSection';
import { DraftAgentRow } from '../DraftAgentRow';
import type { AgentDraft } from '@/lib/types';

interface DraftsSectionProps {
  drafts: AgentDraft[];
  collapsed: boolean;
  selectedDraftId: string | null;
  onToggle: () => void;
  onSelect: (draft: AgentDraft) => void;
  onDelete: (draft: AgentDraft) => void;
}

/**
 * Drafts pinned in their own section at the top of the sidebar, above Pinned
 * and the time-bucketed groups, instead of interleaved by recency into
 * whichever "Today"/"Yesterday" bucket they happen to fall in — a draft is
 * unstarted work, not a session, so it stays visually distinct and easy to
 * find regardless of when it was last touched.
 */
export function DraftsSection({
  drafts,
  collapsed,
  selectedDraftId,
  onToggle,
  onSelect,
  onDelete,
}: DraftsSectionProps) {
  if (!drafts.length) return null;
  return (
    <SidebarSection
      label="Drafts"
      icon={<FilePen className="h-3 w-3 shrink-0 text-muted-foreground" />}
      iconPosition="after"
      open={!collapsed}
      onOpenChange={() => onToggle()}
      data-testid="agent-sidebar-section-drafts"
      triggerTestId="agent-sidebar-section-toggle-drafts"
    >
      <AnimatePresence initial={false}>
        {drafts.map((draft) => (
          <motion.div key={draft.id} {...staggerItem}>
            <DraftAgentRow
              draft={draft}
              selected={selectedDraftId === draft.id}
              onSelect={onSelect}
              onDelete={onDelete}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </SidebarSection>
  );
}
